// @flow
import * as React from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { List, ListItem, Popper } from '@material-ui/core';
import { getDataProvidesProps } from '../../../utils/constants';
import { makeStyles } from '@material-ui/core/styles';
import Theme from './theme';
import TypographyTheme from '../../../typography/theme';
import type { DataProvidesProps } from '../../../utils/constants';
import { type UseSingleDropdownProps } from './use_single_dropdown';
import _ from 'underscore';
import TranslationsContext from '../../../translations/translations_context';
import LoadingSpinner from '../../loading_spinner';

type FlyoutProps = {
  dropdownProps: UseSingleDropdownProps,
  anchorEl?: HTMLElement | any | (() => void),
  noResultsComponent?: React.Node,
  ...DataProvidesProps,
};

const useFlyoutStyles = makeStyles({
  list: {
    backgroundColor: Theme.list.backgroundColor,
    border: `1px solid ${Theme.list.borderColor}`,
    borderTopWidth: '0',
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    boxShadow: '0px 2px 6px rgba(21, 55, 44, 0.13)',
    borderRadius: '0px 0px 5px 5px',
  },
  item: {
    color: TypographyTheme.colors.primary,
    fontFamily: TypographyTheme.fonts.sans,
    fontSize: '1rem',
    fontWeight: TypographyTheme.fontWeights.normal,
    lineHeight: '1.125rem',
  },
  highlightedItem: {
    backgroundColor: Theme.listItem.highlightedColor,
  },
  selectedItem: {
    backgroundColor: Theme.listItem.selectedColor,
  },
});

type FlyoutWrapperProps = {
  isOpen: boolean,
  anchorEl?: HTMLElement | any | (() => void),
  children: React.Node,
};

const FlyoutWrapper = ({
  isOpen,
  anchorEl,
  children,
  ...props
}: FlyoutWrapperProps) => {
  return (
    <StyledPopper
      {...props}
      open={isOpen}
      anchorEl={anchorEl}
      disablePortal
      keepMounted
      placement="bottom-start"
      modifiers={{
        flip: {
          enabled: true,
        },
        preventOverflow: {
          enabled: true,
          boundariesElement: 'scrollParent',
        },
        setPopperWidth: {
          enabled: true,
          fn: (data) => {
            const {
              instance: { reference, popper },
            } = data;
            popper.style.width = `${reference.offsetWidth}px`;
            return data;
          },
        },
      }}
    >
      {children}
    </StyledPopper>
  );
};

const ListItems = (props) => {
  const { items, highlightedIndex, selectedItem, classes, getItemProps } =
    props;

  const translations = React.useContext(TranslationsContext);

  return (
    <>
      {items.map((item, index) => {
        const isHighlighted = index === highlightedIndex;
        const isSelected = item.label === selectedItem?.label;
        const className = (() => {
          if (isHighlighted) {
            return `${classes.item} ${classes.highlightedItem}`;
          } else if (isSelected) {
            return `${classes.item} ${classes.selectedItem}`;
          } else {
            return classes.item;
          }
        })();

        const displayLabel = item.userCreated
          ? `${translations.birch.dropdowns.create_item} '${item.label}'`
          : item.label;

        return (
          <ListItem
            key={`${item.value}${index}`}
            className={className}
            {...getItemProps({ item: item.value, index })}
          >
            {displayLabel}
          </ListItem>
        );
      })}
    </>
  );
};

const BaseFlyout = ({
  dropdownProps,
  anchorEl,
  noResultsComponent,
  ...props
}: FlyoutProps) => {
  const {
    getMenuProps,
    getItemProps,
    highlightedIndex,
    isOpen,
    items,
    selectedItem,
    loadingStatus,
  } = dropdownProps;

  const classes = useFlyoutStyles();

  return (
    <FlyoutWrapper
      anchorEl={anchorEl}
      isOpen={isOpen}
      {...getDataProvidesProps(props)}
    >
      <List className={classes.list} {...getMenuProps()}>
        {loadingStatus === 'error' && <BaseFlyout.LoadingError />}
        {loadingStatus === 'loading' && <BaseFlyout.Loading />}
        {loadingStatus === 'loaded' && (
          <>
            {_.isEmpty(items) && (
              <BaseFlyout.NoResultsFound
                className={classes.item}
                noResultsComponent={noResultsComponent}
              />
            )}
            <ListItems
              items={items}
              highlightedIndex={highlightedIndex}
              selectedItem={selectedItem}
              classes={classes}
              getItemProps={getItemProps}
            />
          </>
        )}
      </List>
    </FlyoutWrapper>
  );
};

const StyledPopper: StyledComponent<*, *, *> = styled(Popper)`
  z-index: 1;
`;

type NoResultsFoundProps = {
  noResultsComponent?: React.Node,
  className: string,
};

const NoResultsFound = (props: NoResultsFoundProps) => {
  const { className, noResultsComponent } = props;

  const translations = React.useContext(TranslationsContext);

  return (
    <ListItem className={className} data-provides={'no-results-found-item'}>
      {noResultsComponent || translations.birch.dropdowns.no_results}
    </ListItem>
  );
};
BaseFlyout.NoResultsFound = NoResultsFound;
BaseFlyout.NoResultsFound.displayName = 'BaseFlyout.NoResultsFound';
BaseFlyout.NoResultsFound.defaultProps = { children: 'No Results Found' };

const CenteredListItem = styled(ListItem)`
  && {
    display: flex;
    justify-content: center;
  }
`;
BaseFlyout.Loading = () => {
  return (
    <CenteredListItem>
      <LoadingSpinner size="small" />
    </CenteredListItem>
  );
};
BaseFlyout.Loading.displayName = 'BaseFlyout.Loading';

const ErrorListItem = styled(ListItem)`
  color: ${Theme.listItem.errorColor};
`;
const LoadingError = () => {
  const translations = React.useContext(TranslationsContext);

  return (
    <ErrorListItem>
      {translations.birch.dropdowns.error_loading_results}
    </ErrorListItem>
  );
};
BaseFlyout.LoadingError = LoadingError;
BaseFlyout.LoadingError.displayName = 'BaseFlyout.LoadingError';

export default BaseFlyout;
